// The two record drawers the buyer works out of.
//
// Each renders the SAME body its full page renders — `ListingDetailBody` and
// `SubmissionDetailBody` — so a listing glanced at from a shortlist and a
// listing opened at its own address can never show different facts, and a fix
// to one is a fix to both. Neither body is touched here; both are consumed
// exactly as their own pages consume them.
//
// TAB STATE IS LOCAL, DELIBERATELY. The URL's single `tab` key belongs to the
// buyer page underneath; a drawer writing to it would contend for the key and
// leave the buyer on the drawer's tab after closing. The open RECORD is the URL
// state — that is the shareable fact — and which tab of it is being read is
// not. That also sidesteps the two bodies' one asymmetry: `ListingDetailBody`
// exports a type GUARD (`isListingTabKey`) and `SubmissionDetailBody` a PARSER
// (`parseTabKey`), and a drawer that never parses a raw value needs neither.
//
// Both are mounted conditionally and keyed on the record id by their callers,
// so opening a second record starts it at Overview instead of inheriting the
// previous one's tab, and closing unmounts the body rather than leaving its
// queries subscribed behind a hidden drawer.
//
// The submission drawer takes an optional LOCAL `initialTab` (R7): an action
// that names where it is going — "Draft intro email" → Buyer intro — opens the
// record there. Read once, at mount; never written to the URL.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi } from "@/api/buyers";
import { EntityRecordDrawer } from "@/components/layout/EntityRecordDrawer";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { resolvedShortListingName } from "@/lib/listingDisplay";
import { LinkButton } from "./BuyerSubmissionDetailPage";
import { ListingDetailBody, type TabKey as ListingTabKey } from "./ListingDetailBody";
import {
  SubmissionDetailBody,
  type TabKey as SubmissionTabKey,
} from "./SubmissionDetailBody";
import { SubmissionStatusControl } from "./SubmissionStatusControl";

/** One listing, read over whatever the reader is standing on. */
export function ListingRecordDrawer({
  listingId,
  title,
  subtitle,
  onClose,
}: {
  listingId: string;
  /** The name the ROW already carries — no fetch, and no flash of a placeholder
   *  title while the body loads. A record reached by a hand-edited URL that this
   *  buyer has no row for falls back to the generic noun, and the body reports
   *  the not-found itself. */
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<ListingTabKey>("overview");

  return (
    <EntityRecordDrawer
      open
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      fullPageHref={`/listings/${listingId}`}
      testId="listing-record-drawer"
    >
      <ListingDetailBody listingId={listingId} activeTab={tab} onTabChange={setTab} />
    </EntityRecordDrawer>
  );
}

/** One submission — the negotiation record, not the listing it is against. */
export function SubmissionRecordDrawer({
  submissionId,
  title,
  subtitle,
  initialTab,
  onClose,
}: {
  submissionId: string;
  /** The listing's short name from the ROW the host has loaded — or absent
   *  when it has none (a deep link to a submission on a later page, or opened
   *  before the list arrived). Absent is a fact the drawer acts on, so hosts
   *  pass `undefined` rather than a stand-in noun. */
  title?: string;
  subtitle?: string;
  /** The tab to open on (local state, never the URL). Absent → Overview. */
  initialTab?: SubmissionTabKey | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<SubmissionTabKey>(initialTab ?? "overview");

  // The same read the body runs, on the same key — React Query serves both
  // subscribers from one request, and the header's chip re-renders when a
  // status change invalidates the cache. NOT `getQueryData`: a cache read does
  // not subscribe, and the chip would sit on the old status after the move.
  // (`BuyerSubmissionDetailPage` does exactly this for its own header.)
  const submission = useQuery({
    queryKey: buyerSubmissionKeys.detail(submissionId),
    queryFn: () => buyerSubmissionsApi.get(submissionId),
    enabled: Boolean(submissionId),
  });
  const s = submission.data;

  // §7.4a item 3a's condition (ii): the control renders under a title that
  // NAMES the record. Two sources, either suffices: the host's `title` (from
  // the row it loaded) or, once the record is here, its own listing through the
  // same ladder the host used — which is what titles a deep link to a
  // submission the host has no row for. Neither → the bare noun and NO control:
  // a status moved under "Submission" is a status moved by guess.
  const recordName =
    (s && resolvedShortListingName(s.listing?.displayName, s.listing?.businessName)) ||
    title ||
    null;

  return (
    <EntityRecordDrawer
      open
      onClose={onClose}
      title={recordName ?? "Submission"}
      subtitle={subtitle}
      fullPageHref={`/buyer-submissions/${submissionId}`}
      // `2xl` (max-w-6xl): the Overview renders a `ResponsiveSplit` main beside
      // a 22rem rail. At the default max-w-5xl the main column is ~592px and the
      // five-vital strip can only lay 3 across; max-w-6xl gives it ~720px.
      width="2xl"
      testId="submission-record-drawer"
      /*
        The status control, in the HEADER — permitted by Layout Standard §7.4a
        as amended in v1.8, on its two conditions: this is the submission page's
        own `SubmissionStatusControl` (same mutation, same `Submissions.Manage`
        gate, same modal — never a second form), and the drawer's title names
        the listing whose submission is being moved, which is what answers the
        "doing it to the wrong record" worry the old rule was written around.
        "Open full page" stays: item 3's "must offer the record's own address"
        is unchanged. "Open buyer" is deliberately absent — the drawer is
        already standing on the buyer.

        `s.canEdit` is passed RAW, exactly as the page header passes it; the
        control ANDs the permission itself, so one gate serves both surfaces.
      */
      actions={
        s &&
        recordName && (
          <>
            <SubmissionStatusControl submissionId={s.id} status={s.status} canEdit={s.canEdit} />
            <LinkButton to={`/listings/${s.listingId}`}>Open listing</LinkButton>
          </>
        )
      }
    >
      <SubmissionDetailBody
        submissionId={submissionId}
        activeTab={tab}
        onTabChange={setTab}
      />
    </EntityRecordDrawer>
  );
}
